import { useTranslation } from 'react-i18next';
import type { MemoryPermissionFlags } from './bindingPayload';

export interface MemoryPermissionsFieldsProps {
  value: MemoryPermissionFlags;
  onChange: (next: MemoryPermissionFlags) => void;
  /** layout: default 2-col grid; inline = single column list */
  layout?: 'grid' | 'inline';
  /** When embedded in a parent Card, omit section title and hint */
  variant?: 'standalone' | 'embedded';
  className?: string;
}

export function MemoryPermissionsFields({
  value,
  onChange,
  layout = 'grid',
  variant = 'standalone',
  className = '',
}: MemoryPermissionsFieldsProps) {
  const { t } = useTranslation('integrations');

  const patch = (partial: Partial<MemoryPermissionFlags>) =>
    onChange({ ...value, ...partial });

  const gridClass =
    layout === 'grid'
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : 'space-y-2';

  return (
    <div className={`space-y-3 ${className}`}>
      {variant === 'standalone' && (
        <div>
          <h4 className="text-gray-800 font-medium mb-1">
            {t('bindingSections.memoryOperations.title')}
          </h4>
          <p className="text-gray-500 text-xs mb-2">
            {t('bindingSections.memoryOperations.hint')}
          </p>
        </div>
      )}
      <div className={gridClass}>
        <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={value.can_read}
            onChange={(e) => patch({ can_read: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-700 font-medium">
            {t('bindingSections.memoryOperations.read')}
          </span>
        </label>
        <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={value.can_write}
            onChange={(e) => patch({ can_write: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-700 font-medium">
            {t('bindingSections.memoryOperations.write')}
          </span>
        </label>
        <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={value.can_search}
            onChange={(e) => patch({ can_search: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-700 font-medium">
            {t('bindingSections.memoryOperations.search')}
          </span>
        </label>
        <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={value.can_delete}
            onChange={(e) => patch({ can_delete: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-gray-700 font-medium text-red-600">
            {t('bindingSections.memoryOperations.delete')}
          </span>
        </label>
      </div>
    </div>
  );
}
